// src/rms/pages/SignIn.jsx
// ============================================================================
// Operator sign-in / sign-up
// ============================================================================
// Deliberately plain. A rental operator signing up at the counter wants two
// fields and a button, not a marketing funnel — the pitch already happened on
// the landing page.
// ============================================================================

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { resolveIdentifier, looksLikeCounterName } from "../data/counterLogin";
import { Field, TextInput, Banner } from "../components/ui";
import "../styles/rms.css";
import Brandmark from "../components/Brandmark";

function SignIn() {
  // Renders outside RmsProvider.
  const { t } = useTranslation();
  const { signIn, signUp, authEnforced, requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Three modes, not two: "forgot" is the request half of password recovery.
  // /reset sends people back here with state.mode when their link has died,
  // so the screen has to be able to open straight into it.
  const [mode, setMode] = useState(location.state?.mode === "forgot" ? "forgot" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Return the operator to whatever they were trying to reach.
  const destination = location.state?.from || "/rms";

  const submit = async (e) => {
    e?.preventDefault();

    // Recovery asks for an address and nothing else.
    if (mode === "forgot") {
      if (!email.trim()) return setError(t("signIn.enterEmail", "Enter your email address."));
      if (looksLikeCounterName(email)) {
        // There is no inbox behind a counter login, so there is nowhere to send
        // a link. The owner rotates it from Settings.
        return setError(t("signIn.counterNoReset", "A counter login has no email. Ask the owner to set a new password for it."));
      }
      setBusy(true);
      setError(null);
      const sent = await requestPasswordReset(email.trim());
      setBusy(false);
      if (!sent.success) return setError(sent.error);
      // Deliberately the same sentence whether or not that address has an
      // account. Confirming which addresses are registered is the one thing a
      // form like this must not do.
      setNotice(
        t("signIn.resetSent", "If there is an account for {email}, a link to set a new password is on its way. It expires in an hour.", {
          email: email.trim(),
        })
      );
      return;
    }

    if (!email.trim() || !password) return setError(t("signIn.enterBoth", "Enter an email and password."));
    if (mode === "signup" && password.length < 8) {
      return setError(t("signIn.tooShort", "Use at least 8 characters."));
    }

    setBusy(true);
    setError(null);

    // One field for both. Somebody at a counter types what they were told --
    // an address or the shop's counter name -- and it works either way. A name
    // resolves to the synthetic address that counter login was created with.
    const identifier = mode === "signin" ? resolveIdentifier(email) : email.trim();
    if (!identifier) {
      return setError(t("signIn.badCounterName", "That counter name is too short."));
    }

    const result = mode === "signin" ? await signIn(identifier, password) : await signUp(identifier, password);

    setBusy(false);

    if (!result.success) return setError(result.error);

    if (result.needsConfirmation) {
      setNotice(
        t("signIn.confirmSent", "Check {email} for a confirmation link, then sign in.", { email: email.trim() })
      );
      setMode("signin");
      return;
    }

    navigate(destination, { replace: true });
  };

  // The .rms-auth shell -- mobile-first, centred lockup, 16px fields and 46px
  // controls. Shared with /reset and /join, and the reason it is shared is that
  // this screen and that one are two steps of the same walk: a lockup that
  // changed size and side halfway through read as a different site.
  return (
    <div className="rms-root rms-landing rms-auth">
      <div className="rms-auth-inner">
        <Link to="/" className="rms-auth-brand">
          <Brandmark size="1.35em" />
        </Link>

        <h1 className="rms-page-title">
          {mode === "signin"
            ? t("signIn.signIn", "Sign in")
            : mode === "forgot"
              ? t("signIn.forgotTitle", "Reset your password")
              : t("signIn.createAccountTitle", "Create your account")}
        </h1>
        <p className="rms-page-sub" style={{ marginBottom: 20 }}>
          {mode === "signin"
            ? t("signIn.signInSub", "Manage your rentals, bookings and invoices.")
            : mode === "forgot"
              ? t("signIn.forgotSub", "Give us the address you sign in with and we will send a link to set a new one.")
              : t("signIn.signUpSub", "Free forever. Pick your business type next.")}
        </p>

        <form className="rms-card rms-card-pad" onSubmit={submit}>
          {!authEnforced ? (
            <Banner tone="info">
              {t("signIn.runningOnTheLocal", "Running on the local backend — no account is needed. Continue straight to")}{" "}
              <Link to="/rms">{t("reset.theApp", "the app")}</Link>.
            </Banner>
          ) : null}

          {error ? <Banner tone="danger">{error}</Banner> : null}
          {notice ? <Banner tone="ok">{notice}</Banner> : null}

          {/* type="text" in sign-in, because a counter name is not an email and
              the browser would refuse to submit it. Sign-up still wants a real
              address and says so. */}
          <Field
            label={mode === "signup" ? t("signIn.email", "Email") : t("signIn.emailOrCounter", "Email or counter name")}
            required
            help={
              mode === "signin" && email.trim() && looksLikeCounterName(email)
                ? t("signIn.usingCounter", "Signing in with a shared counter login.")
                : undefined
            }
          >
            <TextInput
              type={mode === "signup" ? "email" : "text"}
              value={email}
              onChange={setEmail}
              autoComplete={mode === "signup" ? "email" : "username"}
            />
          </Field>

          {mode === "forgot" ? null : (
            <Field
              label={t("signIn.password", "Password")}
              required
              help={mode === "signup" ? t("signIn.atLeast", "At least 8 characters.") : undefined}
            >
              <input
                className="rms-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
              />
            </Field>
          )}

          <button type="submit" className="rms-btn rms-btn--primary" style={{ width: "100%" }} disabled={busy}>
            {busy
              ? t("common.working", "Working…")
              : mode === "signin"
                ? t("signIn.signIn", "Sign in")
                : mode === "forgot"
                  ? t("signIn.sendResetLink", "Send the link")
                  : t("signIn.createAccount", "Create account")}
          </button>

          {/* Under the button, not beside the password field. Somebody who
              cannot get in reads down the form and finds it at the point they
              have run out of other options. */}
          {mode === "signin" && authEnforced ? (
            <p className="rms-help" style={{ textAlign: "center", marginTop: 14, marginBottom: 0 }}>
              <button
                type="button"
                className="rms-linkish rms-linkish--tap"
                onClick={() => { setMode("forgot"); setError(null); setNotice(null); setPassword(""); }}
              >
                {t("signIn.forgotPassword", "Forgot your password?")}
              </button>
            </p>
          ) : null}
        </form>

        <p className="rms-help" style={{ textAlign: "center", marginTop: 16 }}>
          {mode === "forgot" ? (
            <>
              {t("signIn.rememberedIt", "Remembered it?")}{" "}
              <button type="button" className="rms-linkish" onClick={() => { setMode("signin"); setError(null); }}>
                {t("landing.signIn", "Sign in")}
              </button>
            </>
          ) : mode === "signin" ? (
            <>
              {t("signin.noAccount", "No account yet?")}{" "}
              <button type="button" className="rms-linkish" onClick={() => { setMode("signup"); setError(null); }}>
                {t("signIn.createOneFree", "Create one free")}
              </button>
            </>
          ) : (
            <>
              {t("signin.haveAccount", "Already have an account?")}{" "}
              <button type="button" className="rms-linkish" onClick={() => { setMode("signin"); setError(null); }}>
                {t("landing.signIn", "Sign in")}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export default SignIn;
